import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download } from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";
import { intakeSections } from "@/lib/intakeSections";

export default function IntakePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [inq, setInq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/inquiries`, { withCredentials: true });
        setInq(data.find((i) => i.id === id) || null);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, id]);

  if (user === false) return <Navigate to="/admin" replace />;
  if (user === null || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="intake-page-loading">
        <p className="text-slate-500 text-sm">Loading…</p>
      </div>
    );
  }
  if (!inq) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4" data-testid="intake-page-missing">
        <p className="text-slate-400">Intake not found.</p>
        <Link to="/admin" className="text-amber-300 text-sm hover:text-amber-200">← Back to inbox</Link>
      </div>
    );
  }

  const sections = inq.intake ? intakeSections(inq.intake) : [];

  return (
    <div data-testid="intake-page" className="min-h-screen bg-black px-4 sm:px-6 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="relative flex items-center justify-between mb-10">
          <Link to="/admin" data-testid="intake-back-btn"
            className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-amber-300 border border-white/10 rounded-full px-4 py-2 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Inbox
          </Link>
          <img src="/assets/logo-icon.png" alt="M&M" className="absolute left-1/2 -translate-x-1/2 h-8 w-auto" />
        </div>

        <div className="notched border border-amber-400/30 bg-[#12141C] p-6 sm:p-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400/80 mb-2">Project Intake</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="intake-page-name">{inq.name}</h1>
          <p className="mt-2 text-sm text-slate-400">
            {inq.email && <a href={`mailto:${inq.email}`} className="text-amber-300/90 hover:text-amber-200 transition-colors">{inq.email}</a>}
            {inq.email && inq.phone && <span> · </span>}
            {inq.phone && <a href={`tel:${inq.phone}`} className="text-amber-300/90 hover:text-amber-200 transition-colors">{inq.phone}</a>}
          </p>
          <p className="mt-1 text-xs text-slate-500">{new Date(inq.created_at).toLocaleString()}</p>

          <div className="mt-10 flex flex-col gap-8">
            {sections.map((sec) => (
              <div key={sec.title} data-testid={`intake-page-section-${sec.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-xs uppercase tracking-[0.25em] text-amber-400/80 mb-3 border-b border-amber-400/20 pb-2">{sec.title}</p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                  {sec.rows.map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <dt className="text-xs text-slate-500">{k}</dt>
                      <dd className="text-sm text-slate-100 break-words">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
            {sections.length === 0 && (
              <p className="text-sm text-slate-400 whitespace-pre-wrap">{inq.details}</p>
            )}
          </div>

          {inq.files && inq.files.length > 0 && (
            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-400/80 mb-3 border-b border-amber-400/20 pb-2">Files</p>
              <div className="flex flex-wrap gap-2" data-testid="intake-page-files">
                {inq.files.map((fl) => (
                  <a key={fl.path} href={`${API}/files/${fl.path}`} data-testid={`intake-file-${fl.name}`}
                    className="inline-flex items-center gap-2 notched-sm border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10 transition-colors">
                    <Download className="h-3.5 w-3.5" /> {fl.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
